import {
  Flex,
  Group,
  Paper,
  Text,
  Title,
  Button,
  Switch,
  ActionIcon,
  Modal,
  Badge,
  Box,
  Divider,
  Stack,
  Tooltip,
} from "@mantine/core";
import {
  IconPrinter,
  IconDownload,
  IconWorld,
  IconLock,
  IconQrcode,
} from "@tabler/icons-react";

import { printQR } from "../../../utils/printEventQR";
import { createQrCode } from "../../../utils/createQRcode";
import { useEffect, useRef } from "react";
import { usePatchEvent } from "../../../api/event";
import { useDisclosure } from "@mantine/hooks";
import CopyableUrl from "../../CopyableUrl";

const EventVisibility = ({ event }) => {
  const qrModalRef = useRef(null);

  const eventUrl = `${window.location.origin}/public-event/${event.slug || event.id}`;

  const [opened, { open, close }] = useDisclosure(false);

  useEffect(() => {
    if (opened && event.isPublic) {
      const timer = setTimeout(() => {
        if (qrModalRef.current) {
          qrModalRef.current.innerHTML = "";
          const qrCodeLarge = createQrCode(300, eventUrl);
          qrCodeLarge.append(qrModalRef.current);
        }
      }, 50);
      //Small delay to allow modal DOM to be ready. Change value if needed.
      return () => clearTimeout(timer);
    }
  }, [opened, event.id, event.isPublic, eventUrl]);

  const handleDownloadClick = () => {
    const qrCodeDownload = createQrCode(1000, eventUrl);
    qrCodeDownload.download({
      extension: "png",
    });
  };

  const handlePrintClick = () => {
    const qrCodePrint = createQrCode(2000, eventUrl);
    printQR(qrCodePrint);
  };

  const { mutate, isPending } = usePatchEvent(event.id);

  // Update event isPublic
  const handleChangeEventStatus = (e) => {
    mutate({ isPublic: e.currentTarget.checked });
  };

  const hasDates = event?.dates?.length > 0;
  const isDisabled = !hasDates || isPending;

  return (
    <Flex direction="column">
      <Flex
        direction="row"
        justify="space-between"
        align="center"
        mt="sm"
        mb="xs"
      >
        <Group align="center" gap="sm">
          <Title order={2}>Event Visibility</Title>
        </Group>
      </Flex>

      <Paper withBorder p="lg" shadow="sm" radius="md">
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Make your event publicly available. Agenda, Speakers, and Sponsors
            will be visible to anyone without logging in.
          </Text>
          <Group gap="sm">
            <Tooltip
              label={
                !hasDates
                  ? "Event dates must be set before enabling public page"
                  : "Updating event visibility..."
              }
              disabled={!isDisabled}
            >
              <span style={{ display: "inline-block" }}>
                <Switch
                  checked={event.isPublic}
                  onChange={handleChangeEventStatus}
                  disabled={isDisabled}
                  size="md"
                  color="green"
                />
              </span>
            </Tooltip>
            <Badge
              variant="light"
              color={event.isPublic ? "green" : "gray"}
              size="lg"
              leftSection={
                event.isPublic ? (
                  <IconWorld size={14} />
                ) : (
                  <IconLock size={14} />
                )
              }
            >
              {event.isPublic ? "Public" : "Private"}
            </Badge>
          </Group>
        </Stack>

        {event.isPublic && (
          <>
            <Divider my="lg" />

            <Stack gap="md">
              <Group gap="xs">
                <Text fw={600} size="sm">
                  Share your event
                </Text>
              </Group>

              <CopyableUrl url={eventUrl} />

              <Group gap="sm">
                <Button
                  variant="light"
                  color="green"
                  leftSection={<IconQrcode size={18} />}
                  onClick={open}
                  radius="md"
                >
                  View QR Code
                </Button>

                <Tooltip label="Print QR Code" withArrow>
                  <ActionIcon
                    variant="light"
                    color="green"
                    onClick={handlePrintClick}
                    size="lg"
                    radius="md"
                  >
                    <IconPrinter size={24} />
                  </ActionIcon>
                </Tooltip>

                <Tooltip label="Download QR Code" withArrow>
                  <ActionIcon
                    variant="light"
                    color="green"
                    size="lg"
                    radius="md"
                    onClick={handleDownloadClick}
                  >
                    <IconDownload size={24} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Stack>

            {/* Modal */}
            <Modal
              opened={opened}
              onClose={close}
              title={
                <Group gap="xs">
                  <IconQrcode size={22} />
                  <Text fw={600}>Event QR Code</Text>
                </Group>
              }
              centered
              radius="lg"
              size="sm"
            >
              <Stack align="center" gap="lg" py="md">
                <Paper
                  withBorder
                  p="lg"
                  radius="lg"
                  style={{
                    background:
                      "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                  }}
                >
                  <Box ref={qrModalRef} />
                </Paper>

                <Text size="sm" c="dimmed" ta="center">
                  Scan this code to view the public event page
                </Text>

                <Group gap="sm">
                  <Button
                    variant="light"
                    color="green"
                    leftSection={<IconPrinter size={18} />}
                    onClick={handlePrintClick}
                    radius="md"
                  >
                    Print
                  </Button>
                  <Button
                    variant="light"
                    color="green"
                    leftSection={<IconDownload size={18} />}
                    onClick={handleDownloadClick}
                    radius="md"
                  >
                    Download
                  </Button>
                </Group>
              </Stack>
            </Modal>
          </>
        )}
      </Paper>
    </Flex>
  );
};

export default EventVisibility;
